import { memo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AuraLayout,
  Button,
  Layout,
} from "lola-framework-ui-test";
import { SupportV2Icon } from "lola-framework-ui-test/src/icons";
import { BodyCopy } from "../../components/BodyCopy";
import { Toast } from "../../components/Toast";
import {
  GALLERY_NOTICE_FIXTURES,
  GalleryInboxChrome,
  GalleryInboxDrawer,
  GalleryPreferencesSheet,
} from "../../components/notifications/GalleryInbox";
import { TransactionsHistory } from "../../components/TransactionsHistory";
import { M2M_ROUTES } from "../../constants";
import type { ITransaction } from "../../interfaces/transactions";
import { useStore } from "../../store/globalState";
import { TransactionsStatus } from "../../utils/commons";
import { Navbar } from "../../components/Navbar";

const MOCK_ITEMS: ITransaction[] = [
  {
    operationId: "op_m2m_1",
    status: TransactionsStatus.COMPLETED,
    amount: "500.00",
    currency: "MXN",
    date: "02/09/26",
  },
  {
    operationId: "op_m2m_2",
    status: TransactionsStatus.PENDING,
    amount: "250.00",
    currency: "MXN",
    date: "01/09/26",
  },
];

interface TransactionsPageProps {
  originationDisabled?: boolean;
  inboxOpen?: boolean;
  toastOpen?: boolean;
  /** Gallery inspectable: open the preferences sheet on first paint. */
  preferencesOpen?: boolean;
}

export const TransactionsPage = memo(
  ({
    originationDisabled = false,
    inboxOpen = false,
    toastOpen = false,
    preferencesOpen: preferencesOpenProp = false,
  }: TransactionsPageProps) => {
    const { t } = useTranslation("m2m");
    const { theme } = useStore();
    const navigate = useNavigate();
    const colorConfig = theme.whiteView;
    const { title, backgroundBtn, textColorBtn, dropzoneColors, bodyCopy } =
      colorConfig;
    const showBell = !originationDisabled;
    const [noticesOpen, setNoticesOpen] = useState(inboxOpen);
    const [preferencesOpen, setPreferencesOpen] = useState(preferencesOpenProp);
    const [readIds, setReadIds] = useState<string[]>([]);
    const [archivedIds, setArchivedIds] = useState<string[]>([]);
    const notices = GALLERY_NOTICE_FIXTURES.filter(
      (item) => !archivedIds.includes(item.id),
    ).map((item) => ({
      id: item.id,
      title: t(item.titleKey),
      body: t(item.bodyKey),
      isRead: item.read || readIds.includes(item.id),
      createdAt: item.createdAt,
    }));

    const goToTransfer = () => {
      if (originationDisabled) return;
      navigate(M2M_ROUTES.TRANSFER_A);
    };

    return (
      <>
      <AuraLayout colorConfig={colorConfig} className="white-view-background flavor-a">
        <Layout.Header>
          <Navbar
            title={t("transactions.nav.title")}
            align="center"
            onBackClick={() => navigate(-1)}
            icon={
              showBell ? (
                <GalleryInboxChrome
                  ns="m2m"
                  unread={notices.filter((item) => !item.isRead).length}
                  bellColor={title}
                  badgeBackground={backgroundBtn}
                  badgeTextColor={textColorBtn}
                  onOpen={() => setNoticesOpen(true)}
                />
              ) : (
                <SupportV2Icon
                  className="inverted-icon"
                  colors={dropzoneColors}
                />
              )
            }
            onIconClick={
              showBell ? undefined : () => navigate(M2M_ROUTES.SUPPORT_V2)
            }
          />
        </Layout.Header>
        <Layout.Content className="transactions-section flavor-a">
          <TransactionsHistory
            items={MOCK_ITEMS}
            title={t("transactions.title")}
          />
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section transactions-footer">
            {originationDisabled && (
              <BodyCopy
                textColor={bodyCopy}
                className="transactions-disabled-note"
              >
                {t("transactions.transfersDisabled")}
              </BodyCopy>
            )}
            <Button
              size="small"
              variant="cancel"
              background={backgroundBtn}
              color={backgroundBtn}
              className="alreary-acc-btn"
              disabled={originationDisabled}
              onClick={goToTransfer}
            >
              {t("transactions.backHome")}
            </Button>
            <Button
              size="small"
              background={backgroundBtn}
              color={textColorBtn}
              disabled={originationDisabled}
              onClick={goToTransfer}
            >
              {t("transactions.transfer")}
            </Button>
          </section>
        </Layout.Footer>
      </AuraLayout>
      {showBell ? (
        <>
        <GalleryInboxDrawer
          ns="m2m"
          items={notices}
          open={noticesOpen}
          onClose={() => setNoticesOpen(false)}
          onMarkRead={(id) =>
            setReadIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onArchive={(id) =>
            setArchivedIds((ids) => (ids.includes(id) ? ids : [...ids, id]))
          }
          onOpenPreferences={() => {
            setNoticesOpen(false);
            setPreferencesOpen(true);
          }}
        />
        <GalleryPreferencesSheet
          ns="m2m"
          open={preferencesOpen}
          onClose={() => setPreferencesOpen(false)}
        />
        </>
      ) : null}
      {toastOpen ? (
        <Toast message={t("notifications.exampleToast")} type="info" persist sound />
      ) : null}
      </>
    );
  }
);

export const UserRestrictedTransactionsPage = memo(() => (
  <TransactionsPage originationDisabled />
));

export const InboxPage = memo(() => <TransactionsPage inboxOpen />);

export const PreferencesPage = memo(() => <TransactionsPage preferencesOpen />);

export const ToastPage = memo(() => <TransactionsPage toastOpen />);
